import { CircleNotch, MagnifyingGlass, Plus } from "@phosphor-icons/react";
import { useState } from "react";
import { Link } from "react-router";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAdminHouseholds } from "@/lib/api";
import { cn } from "@/lib/utils";

import { AdminShell } from "../admin-shell";
import { CreateHouseholdDialog } from "./create-household-dialog";
import { HouseholdTable } from "./household-table";
import { ImportHouseholdDialog } from "./import-household-dialog";
import { HOUSEHOLD_STATUS_ORDER, householdStatusLabel } from "./status";

// "archived" is a view mode, not a household status — selecting it lists the
// archived homes instead of filtering the active ones by status.
const ARCHIVED_FILTER = "archived";

// Derived from the shared lifecycle definition so filters, pills, and the
// dropdown never drift; "All" and "Archived" bracket the real statuses.
const STATUS_FILTERS: { label: string; value: string | null }[] = [
  { label: "All", value: null },
  ...HOUSEHOLD_STATUS_ORDER.map((value) => ({
    label: householdStatusLabel(value),
    value,
  })),
  { label: "Archived", value: ARCHIVED_FILTER },
];

export function AdminHouseholdsPage() {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const isArchivedView = statusFilter === ARCHIVED_FILTER;
  const householdsQuery = useAdminHouseholds(isArchivedView || undefined);

  const households = householdsQuery.data ?? [];
  const filtered = households.filter((h) => {
    const q = search.toLowerCase();
    const matchesSearch =
      h.name.toLowerCase().includes(q) || h.contact_email.toLowerCase().includes(q);
    // In the archived view the query already returns only archived rows, so the
    // status chips don't further narrow it; otherwise apply the status filter.
    const matchesStatus = isArchivedView || statusFilter === null || h.status === statusFilter;
    return matchesSearch && matchesStatus;
  });
  const readyCount = households.filter((h) => h.readiness_percent === 100).length;
  const supportCount = households.filter((h) => h.status === "support_needed").length;

  return (
    <AdminShell
      title="Households"
      actions={
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            className="text-muted hover:text-primary text-[14px] font-medium transition-colors"
          >
            Add manually
          </button>
          <Button onClick={() => setImportOpen(true)}>
            <Plus className="mr-2 h-4 w-4" weight="light" />
            New household
          </Button>
        </div>
      }
    >
      {householdsQuery.isLoading ? (
        <div className="bg-card shadow-elevation flex justify-center rounded-md py-16">
          <CircleNotch className="text-muted h-6 w-6 animate-spin" weight="light" />
        </div>
      ) : householdsQuery.isError ? (
        <div className="bg-card shadow-elevation rounded-md py-12 text-center">
          <p className="text-muted text-[15px]">
            Could not load households. Please{" "}
            <button
              type="button"
              onClick={() => void householdsQuery.refetch()}
              className="text-accent underline"
            >
              try again
            </button>{" "}
            or{" "}
            <Link to="/home" className="text-accent underline">
              go back
            </Link>
            .
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <p className="text-muted -mt-2 text-[14px]">
            {households.length} homes · <b className="text-primary">{readyCount}</b> ready to hand
            off
            {supportCount > 0 && (
              <>
                {" · "}
                <b className="text-destructive">{supportCount}</b>{" "}
                <span className="text-destructive">
                  {supportCount === 1 ? "needs support" : "need support"}
                </span>
              </>
            )}
          </p>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="relative max-w-md flex-1">
              <MagnifyingGlass
                className="text-muted absolute top-1/2 left-3.5 h-4 w-4 -translate-y-1/2"
                weight="light"
              />
              <Input
                placeholder="Search households..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-11 pl-10"
              />
            </div>
            <div className="flex flex-wrap gap-1.5">
              {STATUS_FILTERS.map((filter) => (
                <button
                  key={filter.label}
                  type="button"
                  onClick={() => setStatusFilter(filter.value)}
                  className={cn(
                    "focus-visible:outline-ring rounded-sm px-3.5 py-1.5 text-[13px] font-semibold transition-colors focus-visible:outline-2 focus-visible:outline-offset-2",
                    statusFilter === filter.value
                      ? "bg-primary text-white"
                      : "border-border text-muted border bg-white",
                  )}
                >
                  {filter.label}
                </button>
              ))}
            </div>
          </div>
          <HouseholdTable households={filtered} />
          <CreateHouseholdDialog open={createOpen} onOpenChange={setCreateOpen} />
          <ImportHouseholdDialog open={importOpen} onOpenChange={setImportOpen} />
        </div>
      )}
    </AdminShell>
  );
}
